import axios from "axios";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "http://localhost:8000").replace(/\/$/, "");
export const API = `${BACKEND_URL}/api`;

const DATE_KEYS = new Set([
  "start_date",
  "end_date",
  "campaign_start",
  "campaign_end",
  "expected_end_date",
  "payment_due_date",
  "invoice_date",
  "due_date",
  "payment_date",
  "subscription_start",
  "subscription_expiry",
  "expires_at",
]);

function toApiDate(value) {
  if (!value) return null;
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return `${value}T00:00:00Z`;
  }
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) {
    return new Date(value).toISOString();
  }
  return value;
}

function normalizeRequestData(value) {
  if (!value || typeof value !== "object") return value;
  if (Array.isArray(value)) return value.map(normalizeRequestData);

  const next = {};
  Object.entries(value).forEach(([key, item]) => {
    if (DATE_KEYS.has(key)) {
      next[key] = toApiDate(item);
    } else if (key === "photos" && Array.isArray(item)) {
      next.images = item.map((photo) => (typeof photo === "string" ? photo : photo?.url)).filter(Boolean);
      next.photos = item;
    } else {
      next[key] = normalizeRequestData(item);
    }
  });
  return next;
}

function normalizeResponseData(value) {
  if (!value || typeof value !== "object") return value;
  if (typeof Blob !== "undefined" && value instanceof Blob) return value;
  if (Array.isArray(value)) return value.map(normalizeResponseData);

  const next = { ...value };
  if (!next.photos && Array.isArray(next.images)) {
    next.photos = next.images.map((url) => (typeof url === "string" ? { url, public_id: "" } : url));
  }
  Object.entries(next).forEach(([key, item]) => {
    if (item && typeof item === "object") next[key] = normalizeResponseData(item);
  });
  return next;
}

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

let pendingRequests = 0;

function emitLoading() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("hx:api-loading", { detail: { loading: pendingRequests > 0 } }));
  }
}

function startLoading(config) {
  if (!config?.skipGlobalLoader) {
    pendingRequests += 1;
    emitLoading();
    config.__hxGlobalLoader = true;
  }
}

function stopLoading(config) {
  if (config?.__hxGlobalLoader) {
    pendingRequests = Math.max(0, pendingRequests - 1);
    emitLoading();
  }
}

api.interceptors.request.use((config) => {
  startLoading(config);
  const token = localStorage.getItem("hx_token");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  const method = String(config.method || "get").toUpperCase();
  if (sessionStorage.getItem("hx_admin_token") && ["POST", "PUT", "PATCH", "DELETE"].includes(method)) {
    const path = `${config.url || ""}`;
    if (!path.includes("/auth/logout") && !path.includes("/ghost-session")) {
      stopLoading(config);
      return Promise.reject({ response: { data: { detail: "Ghost session is read-only" }, status: 403 }, config });
    }
  }
  if (config.data && !(config.data instanceof FormData)) {
    config.data = normalizeRequestData(config.data);
  }
  return config;
});

api.interceptors.response.use((response) => {
  stopLoading(response.config);
  response.data = normalizeResponseData(response.data);
  return response;
}, (error) => {
  stopLoading(error.config);
  return Promise.reject(error);
});

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
